import React from "react";
import { IconButton, mergeStyleSets } from "@fluentui/react";
import { GridCellProps } from "@progress/kendo-react-grid";
import { TextField } from "@fluentui/react/lib/TextField";
import { sanitizeUserInput } from "../../../utils/helpers/CustomTrim";
import { TgkDropDownItem } from "../../interfaces/CoObject";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { generateIdentifier } from "../../../utils/helpers/RobotFramework";

const contentStyles = mergeStyleSets({
  rowButton: {
    width: "10px",
    float: "center",
  },
  rowDrag: {
    width: "20px",
  },
  keyValueHeaderStyles: {
    display: "flex",
    gap: "0.5rem",
    backgroundColor: "white",
    height: "100%",
    fontSize: "1.125rem",
    fontWeight: "medium",
  },
});

export interface CommandCellProps {
  remove: (dataItem: TgkDropDownItem) => void;
  editField: string;
  isRemovable: boolean;
  isLocked: boolean;
}

export interface KeyValueInputCellProps {
  isLocked: boolean;
}

export const DragHandleCell = () => {
  return (
    <td className={contentStyles.rowDrag}>
      <span className="k-icon k-i-reorder" style={{ cursor: "move", height: "1.75rem" }} data-drag-handle={true} />
    </td>
  );
};

export const CommandCell =
  ({ editField, remove, isRemovable, isLocked }: CommandCellProps) =>
  // eslint-disable-next-line react/display-name
  ({ dataItem }: GridCellProps) => {
    const inEdit = dataItem[editField];
    return inEdit ? (
      <td className={contentStyles.rowButton}>
        <IconButton
          data-testid="remove-btn"
          iconProps={{ iconName: "Delete" }}
          style={{ color: "var(--color-black)", height: "1.75rem" }}
          disabled={!isRemovable || isLocked}
          onClick={() => isRemovable && remove(dataItem)}
        />
      </td>
    ) : null;
  };

export const KeyValueInputCell = (gridCellProps: GridCellProps) => {
  const { onChange, dataIndex } = gridCellProps;
  const dataItem: TgkDropDownItem = gridCellProps.dataItem;

  const handleChange = (e, newValue, type: "key" | "value") => {
    if (onChange) {
      onChange({
        dataItem,
        dataIndex,
        syntheticEvent: e.syntheticEvent,
        field: type,
        value: newValue,
      });
    }
  };

  const setBlurred = (type: "key" | "value") => {
    if (onChange) {
      onChange({
        dataItem,
        dataIndex,
        syntheticEvent: undefined,
        field: type === "key" ? "isBlurredKey" : "isBlurredValue",
        value: true,
      });
    }
  };

  const dataValueKey = dataItem.key;
  const dataValueValue = dataItem.value;

  const isValidKey = !dataItem.keyError || !dataItem.isBlurredKey;
  const isValidValue = !dataItem.valueError || !dataItem.isBlurredValue;

  const keyError = dataItem.isBlurredKey && dataItem.keyError;
  const valueError = dataItem.isBlurredValue && dataItem.valueError;

  return (
    <td style={{ position: "relative", overflow: "visible" }}>
      {dataItem.inEdit ? (
        <div style={{ display: "flex", gap: "5px", width: "100%" }}>
          <TextField
            id={generateIdentifier(gridCellProps.dataIndex + "", "key")}
            onChange={(e, newValue) => handleChange(e, newValue, "key")}
            className={"Flex1TextField " + (isValidKey ? "ValidTextField" : "InvalidTextField")}
            value={dataValueKey ?? ""}
            onBlur={(e) => {
              const newValue = sanitizeUserInput(e.target.value);
              if (newValue !== e.target.value) handleChange(e, newValue, "key");
              if (!dataItem.isBlurredKey) setBlurred("key");
            }}
          />
          <TextField
            id={generateIdentifier(gridCellProps.dataIndex + "", "value")}
            onChange={(e, newValue) => handleChange(e, newValue, "value")}
            className={"Flex1TextField " + (isValidValue ? "ValidTextField" : "InvalidTextField")}
            value={dataValueValue ?? ""}
            onBlur={(e) => {
              const newValue = sanitizeUserInput(e.target.value);
              if (newValue !== e.target.value) handleChange(e, newValue, "value");
              if (!dataItem.isBlurredValue) setBlurred("value");
            }}
          />
        </div>
      ) : (
        dataValueKey.toString() + " " + dataValueValue.toString()
      )}

      <span
        style={{
          color: "var(--color-red)",
          fontSize: "10px",
          position: "absolute",
          lineHeight: "1",
          bottom: "-8px",
          left: "20px",
        }}
      >
        {keyError || valueError}
      </span>
    </td>
  );
};

export const KeyValueHeaderCell = () => (
  <div className={contentStyles.keyValueHeaderStyles}>
    <span style={{ flex: 1 }}>{formatMessage("cof.base.displayname")}</span>
    <span style={{ flex: 1 }}>{formatMessage("cof.base.value")}</span>
  </div>
);

//KEEP FOR FUTURE GRID IMPLEMENTATION
export const InputCell = (gridCellProps: GridCellProps) => {
  const { onChange, dataItem, dataIndex } = gridCellProps;
  const field = gridCellProps.field ? gridCellProps.field : "";

  const handleChange = (e, newValue) => {
    if (onChange) {
      onChange({
        dataItem,
        dataIndex,
        syntheticEvent: e.syntheticEvent,
        field: gridCellProps.field,
        value: newValue,
      });
    }
  };

  const setBlurred = () => {
    if (onChange) {
      onChange({
        dataItem,
        dataIndex,
        syntheticEvent: undefined,
        field: gridCellProps.field === "key" ? "isBlurredKey" : "isBlurredValue",
        value: true,
      });
    }
  };
  const dataValue = dataItem[field] === null ? "" : dataItem[field];

  const isValidKey = !dataItem.keyError || !dataItem.isBlurredKey;
  const isValidValue = !dataItem.valueError || !dataItem.isBlurredValue;
  const isValid = field === "key" ? isValidKey : isValidValue;

  const keyError = dataItem.isBlurredKey && dataItem.keyError;
  const valueError = dataItem.isBlurredValue && dataItem.valueError;

  const errorToDisplay = keyError || valueError;

  return (
    <td style={{ position: "relative", overflow: "visible" }}>
      {dataItem.inEdit ? (
        <TextField
          onChange={handleChange}
          className={isValid ? "ValidTextField" : "InvalidTextField"}
          value={dataValue}
          onBlur={(e) => {
            const newValue = sanitizeUserInput(e.target.value);
            handleChange(e, newValue);
            setBlurred();
          }}
        />
      ) : (
        dataValue.toString()
      )}

      {field === "key" && <span className="UnderInputErrorText">{errorToDisplay}</span>}
    </td>
  );
};
